import { ApolloWrapper } from 'pages/apollo_wrapper'
import ErrorBoundary from 'pages/error_boundary'
import { LoadingPage } from 'pages/loading_page'
import { NotFoundPage } from 'pages/not_found'
import { Login } from 'presentation/login/login'
import { Suspense } from 'react'
import { Route, Routes } from 'react-router-dom'
import {
  RouteDefs,
  useRoutesFn,
} from 'routes/_utils/transform_routes/transform.routes'
import { useHomeRoutes } from './home.routes'
import { projectRouteDefs } from './project.routes'

const SUPPLIER_ORDER = () =>
  import('presentation/supplier_orders/supplier_order/supplier_order_page')
const SUPPLIER_SPECIFICATION = () =>
  import(
    'presentation/supplier_orders/supplier_specification/supplier_specification_page'
  )

const SUPPLIER_ADMIN_PRODUCTS = () =>
  import(
    'presentation/supplier_admin/supplier_admin_products/supplier_admin_products'
  )

const SUPPLIER_ADMIN_LAYOUT = () =>
  import('presentation/supplier_admin/supplier_admin')

// Commented out because we don't have the users page ready
// const SUPPLIER_ADMIN_USERS = () =>
//   import('presentation/supplier_admin/users/supplier_admin_users')

const SUPPLIER_ADMIN_DELIVERIES = () =>
  import('presentation/supplier_admin/deliveries/supplier_admin_deliveries')

const SUPPLIER_ADMIN_MAPPINGS = () =>
  import(
    'presentation/supplier_admin/supplier_admin_mappings/supplier_admin_mappings.view'
  )

const PROFILE = () => import('presentation/profile/profile_page')

export const otherRouteDefs: RouteDefs[] = [
  {
    path: '/confirm-order/:order_id',
    file: SUPPLIER_ORDER,
  },
  {
    path: '/confirm-specification/:specification_id',
    file: SUPPLIER_SPECIFICATION,
  },
  {
    path: '/supplier-admin',
    file: SUPPLIER_ADMIN_LAYOUT,
    children: [
      {
        index: true,
        file: SUPPLIER_ADMIN_PRODUCTS,
      },
      {
        path: 'deliveries',
        file: SUPPLIER_ADMIN_DELIVERIES,
      },
      {
        path: 'mappings',
        file: SUPPLIER_ADMIN_MAPPINGS,
      },
    ],
  },
  {
    path: '/profile',
    file: PROFILE,
  },
]

/**
 * Renders the main routes for the application
 * @param props
 * @category Routes
 */
export function ComstructRoutes(
  props: Readonly<{
    isLoading: boolean
    isAuthenticated: boolean
    token: string | undefined
  }>
): JSX.Element {
  const { isLoading, token, isAuthenticated } = props

  // Show also loading page if the user is authenticated but the token is not yet available.
  // Thus, the user is not redirected to the login page unnecessarily.
  const loading = isLoading || (isAuthenticated && !token)
  if (loading) {
    return <LoadingPage helperButtonsEnabled />
  }

  let route
  if (token) {
    route = (
      <ApolloWrapper token={token}>
        <ErrorBoundary>
          <Suspense fallback={<LoadingPage helperButtonsEnabled />}>
            <RouteWrapper />
          </Suspense>
        </ErrorBoundary>
      </ApolloWrapper>
    )
  } else {
    route = (
      <Routes>
        <Route path='*' element={<Login />} />
      </Routes>
    )
  }

  return route
}

/**
 * A wrapper for the routes to be able to access the user context
 * @category Routes
 */
export function RouteWrapper(): JSX.Element {
  return (
    <Routes>
      {useRoutesFn(useHomeRoutes(), ['invoice_id'])}
      {useRoutesFn(projectRouteDefs, ['project_id', 'invoice_id'])}
      {useRoutesFn(otherRouteDefs)}
      <Route path='*' element={<NotFoundPage />} />
    </Routes>
  )
}
